import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router'
import { PATHS } from '@/navigation/paths'
import { cleanObj } from '@/utils/helperFunctions.jsx'
import { sanitizeSearchQuery } from '@/utils/sanitization.js'
import { dollarsToCents } from '@gearghost/money-utils'
import {
  ListingSortSchema,
  SearchTurnParamSchema,
  pickSearchTurn,
} from '@gearghost/api-schemas/listing'

const SEARCH_FILTERS_KEY = 'gg_last_search_filters'

const NEAREST_FIRST = ListingSortSchema.enum.distance

/**
 * A location someone picks orders the results nearest first, unless they
 * already chose a sort (Tyler, 2026-09-23). It replaced a 50-mile radius
 * that hid every store farther out. Applied where a location is picked or
 * restored, never on every read, so Best Match stays choosable with a
 * location set.
 */
export const withNearestFirst = (updates, currentSort) =>
  currentSort ? updates : { ...updates, sort: NEAREST_FIRST }

/**
 * Clearing the location drops a Distance sort: with no point to measure
 * from, the backend falls back to newest first while the menu still read
 * Distance.
 */
export const withoutNearestFirst = (updates, currentSort) =>
  currentSort === NEAREST_FIRST ? { ...updates, sort: undefined } : updates

export const loadLastSearchFilters = () => {
  try {
    const saved = sessionStorage.getItem(SEARCH_FILTERS_KEY)
    return saved ? JSON.parse(saved) : { startDate: null, endDate: null }
  } catch {
    return { startDate: null, endDate: null }
  }
}

export const persistSearchFilters = (filters) => {
  try {
    const existing = loadLastSearchFilters()
    const { startDate, endDate, currentLat, currentLng } = filters
    // locationLabel is not a URL param so it won't be in filters from the search page —
    // preserve whatever was already saved unless filters explicitly clears it
    const locationLabel = 'locationLabel' in filters
      ? filters.locationLabel
      : existing.locationLabel
    sessionStorage.setItem(SEARCH_FILTERS_KEY, JSON.stringify({ startDate, endDate, currentLat, currentLng, locationLabel }))
  } catch {
    // sessionStorage unavailable — not critical
  }
}

/**
 * URL param config — maps internal filter keys to URL param names.
 *
 * Array params use comma-separated values: ?categories=1,2,3
 * Boolean params use '1'/'0': ?favorites=1
 * Numeric params are plain numbers: ?minPrice=50
 * String params are plain strings: ?q=camera&sort=newest
 */

const ARRAY_PARAMS = {
  categories: 'categories',
  subCategories: 'subCategories',
  brands: 'brands',
  models: 'models',
  vendors: 'vendors',
  amenities: 'amenities',
}

const STRING_PARAMS = {
  sort: 'sort',
  startDate: 'startDate',
  endDate: 'endDate',
}

// No `distance`: the radius left the search (Tyler, 2026-09-23), so an old
// link's ?distance= is ignored rather than sent.
const NUMBER_PARAMS = {
  currentLat: 'lat',
  currentLng: 'lng',
  startPrice: 'minPrice',
  endPrice: 'maxPrice',
}


// noSalesTax is the server's filter, like favorites: it goes out on the wire
// (apiFilter). The two hide toggles are the page's own and never do.
const BOOLEAN_PARAMS = {
  favorites: 'favorites',
  noSalesTax: 'noSalesTax',
  hideUnavailable: 'hideUnavailable',
  hideMyListings: 'hideMyListings',
}

// Reverse lookup: URL param name → internal filter key
const URL_TO_FILTER = {}
for (const [filterKey, urlKey] of Object.entries(ARRAY_PARAMS)) {
  URL_TO_FILTER[urlKey] = { key: filterKey, type: 'array' }
}
for (const [filterKey, urlKey] of Object.entries(STRING_PARAMS)) {
  URL_TO_FILTER[urlKey] = { key: filterKey, type: 'string' }
}
for (const [filterKey, urlKey] of Object.entries(NUMBER_PARAMS)) {
  URL_TO_FILTER[urlKey] = { key: filterKey, type: 'number' }
}
for (const [filterKey, urlKey] of Object.entries(BOOLEAN_PARAMS)) {
  URL_TO_FILTER[urlKey] = { key: filterKey, type: 'boolean' }
}

/**
 * Parse URL search params into filter state object.
 */
const parseSearchParams = (searchParams) => {
  const filters = {
    startDate: null,
    endDate: null,
  }

  for (const [urlParam, value] of searchParams.entries()) {
    if (urlParam === 'q' || urlParam === 'view') continue

    const mapping = URL_TO_FILTER[urlParam]
    if (!mapping) continue

    const { key, type } = mapping

    if (type === 'array') {
      const items = value.split(',').filter(Boolean)
      if (items.length > 0) {
        filters[key] = items
      }
    } else if (type === 'number') {
      const num = parseFloat(value)
      if (!isNaN(num)) {
        filters[key] = num
      }
    } else if (type === 'boolean') {
      filters[key] = value === '1'
    } else {
      // string
      if (value) {
        filters[key] = value
      }
    }
  }

  return filters
}

/**
 * Serialize filter state into URLSearchParams.
 * Only includes non-default, non-empty values.
 */
const filtersToSearchParams = (filters, searchText, view) => {
  const params = new URLSearchParams()

  if (searchText) {
    params.set('q', searchText)
  }

  if (view === 'map') {
    params.set('view', 'map')
  }

  for (const [filterKey, urlKey] of Object.entries(ARRAY_PARAMS)) {
    const value = filters[filterKey]
    if (Array.isArray(value) && value.length > 0) {
      params.set(urlKey, value.join(','))
    }
  }

  for (const [filterKey, urlKey] of Object.entries(STRING_PARAMS)) {
    const value = filters[filterKey]
    if (value) {
      params.set(urlKey, value)
    }
  }

  for (const [filterKey, urlKey] of Object.entries(NUMBER_PARAMS)) {
    const value = filters[filterKey]
    if (value !== null && value !== undefined && !isNaN(value)) {
      params.set(urlKey, String(value))
    }
  }

  for (const [filterKey, urlKey] of Object.entries(BOOLEAN_PARAMS)) {
    if (filters[filterKey]) {
      params.set(urlKey, '1')
    }
  }

  return params
}

/**
 * The search's turn in the fair order (Tyler, 2026-10-27: each new search
 * gets its own, and a search someone comes back to keeps it). The address
 * carries it, so Back returns to the same order, and every page of the
 * search sends it (SearchTurnSchema in @gearghost/api-schemas/listing).
 */
const TURN_PARAM = 'turn'

/**
 * The turn an address carries, or undefined when it carries none, or one
 * that is not a turn (a hand-edited link), which a new one then replaces.
 */
const turnOf = (params) => {
  const raw = params.get(TURN_PARAM)
  if (raw === null) return undefined
  const parsed = SearchTurnParamSchema.safeParse(raw)
  return parsed.success ? parsed.data : undefined
}

/**
 * The turn a rewritten address carries: none on a page that does not
 * rotate; on one that does, a new turn when the rewrite starts a new search
 * (a filter, a search term, a reset), else the address's own, or a new one
 * when it has none.
 */
const turnForRewrite = (prev, { withTurn, newSearch }) => {
  if (!withTurn) return undefined
  if (newSearch) return pickSearchTurn()
  const kept = turnOf(prev)
  return kept === undefined ? pickSearchTurn() : kept
}

/**
 * Rebuild params AND carry forward the standalone page params: vendorId
 * (scopes See-all/storefront arrivals) and orderId (the Add Items page of
 * an order change, docs/plans/order-changes.md item 108). Neither is
 * filter state, so filtersToSearchParams doesn't know about them; every
 * setter used to drop vendorId on the first rewrite (a filter tweak, a
 * search submit, the mount-time location restore), silently un-scoping
 * the results grid, and would drop orderId the same way, losing the order.
 * All hook-internal rewrites go through this wrapper.
 */
const STANDALONE_PARAMS = ['vendorId', 'orderId']

const rebuildSearchParams = (prev, filters, searchText, view, turn) => {
  const params = filtersToSearchParams(filters, searchText, view)
  for (const key of STANDALONE_PARAMS) {
    const value = prev.get(key)
    if (value) {
      params.set(key, value)
    }
  }
  if (turn !== undefined) {
    params.set(TURN_PARAM, String(turn))
  }
  return params
}

/**
 * Build a full URL path for the search-listings page with query params.
 *
 * Used by entry points (home page, category links, header icons) to
 * create shareable URLs that pre-set filters.
 *
 * @param {Object} options
 * @param {string}  [options.q]             - Search text
 * @param {Array}   [options.categories]    - Category IDs
 * @param {Array}   [options.subCategories] - Subcategory IDs
 * @param {Array}   [options.brands]        - Brand IDs
 * @param {Array}   [options.models]        - Model IDs
 * @param {Array}   [options.vendors]       - Vendor IDs
 * @param {string}  [options.startDate]     - Start date (YYYY-MM-DD)
 * @param {string}  [options.endDate]       - End date (YYYY-MM-DD)
 * @param {number}  [options.currentLat]    - Location latitude
 * @param {number}  [options.currentLng]    - Location longitude
 * @param {string}  [options.sort]          - Sort key
 * @param {boolean} [options.favorites]     - Favorites filter
 * @param {boolean} [options.noSalesTax]    - Only listings with no sales tax
 * @param {string}  [options.view]          - 'map' or omitted for list
 * @param {string}  [options.vendorId]      - Single vendor id (cuid string)
 * @param {string}  [options.path]          - Another page that reads the same filters (a category's page,
 *   /los-angeles/<category>); the search page when omitted
 * @returns {string} URL path with query string, e.g. "/search-listings?categories=1,2"
 */
export const buildSearchUrl = (options = {}) => {
  const { path = PATHS.searchListings } = options
  const params = new URLSearchParams()

  if (options.q) {
    params.set('q', options.q)
  }

  if (options.view === 'map') {
    params.set('view', 'map')
  }

  // vendorId is a standalone string param, not part of filters
  if (options.vendorId) {
    params.set('vendorId', options.vendorId)
  }

  // Array params
  for (const key of Object.keys(ARRAY_PARAMS)) {
    const value = options[key]
    if (Array.isArray(value) && value.length > 0) {
      params.set(ARRAY_PARAMS[key], value.join(','))
    }
  }

  // String params
  for (const key of Object.keys(STRING_PARAMS)) {
    const value = options[key]
    if (value) {
      params.set(STRING_PARAMS[key], value)
    }
  }

  // Number params
  for (const key of Object.keys(NUMBER_PARAMS)) {
    const value = options[key]
    if (value !== null && value !== undefined && !isNaN(value)) {
      params.set(NUMBER_PARAMS[key], String(value))
    }
  }

  // Boolean params
  for (const key of Object.keys(BOOLEAN_PARAMS)) {
    if (options[key]) {
      params.set(BOOLEAN_PARAMS[key], '1')
    }
  }

  const qs = params.toString()
  return qs ? `${path}?${qs}` : path
}

/**
 * Hook that manages search page filter state via URL search params.
 *
 * The URL is the source of truth: filters are read from search params
 * on mount, and every filter change updates the URL.
 *
 * Returns the same interface that RentItems previously managed with
 * useState, so SearchHeader can remain unchanged.
 */
/**
 * URL-backed search filter state.
 *
 * @param {Object}  [options]
 * @param {boolean} [options.syncLocationStorage=true] - Restore the last
 *   search location from sessionStorage into the URL on mount, and persist
 *   location changes back. The search page wants this; vendor-scoped pages
 *   (storefront) MUST pass false — a restored location there would switch
 *   one store's listings to a nearest-first sort they all tie on, and
 *   persisting from those pages would clobber the search page's saved
 *   location with nothing.
 * @param {boolean} [options.withTurn=false] - Keep the search's turn in the
 *   fair order in the URL. The search page wants this; pages that list one
 *   store's listings, which never rotate, leave it off.
 */
export const useSearchFilters = ({ syncLocationStorage = true, withTurn = false } = {}) => {
  const [searchParams, setSearchParams] = useSearchParams()

  // Parse URL into filter state (recomputes whenever URL changes)
  const filters = useMemo(
    () => parseSearchParams(searchParams),
    [searchParams]
  )

  const turn = useMemo(
    () => (withTurn ? turnOf(searchParams) : undefined),
    [searchParams, withTurn]
  )

  // Restore saved location from sessionStorage when arriving with a bare URL
  // (e.g. clicking the search icon in the header), and give a search that
  // arrives without a turn its own. Dates are managed by the rentalDates
  // Redux slice, so only location needs sessionStorage restore. Both land in
  // ONE write: setSearchParams rebuilds from the URL this render read, so a
  // second write in the same render drops what the first one added.
  const hasRestored = useRef(false)
  useEffect(() => {
    const firstRun = !hasRestored.current
    hasRestored.current = true

    const saved = firstRun && syncLocationStorage ? loadLastSearchFilters() : null
    const restoresLocation = Boolean(
      saved && !filters.currentLat && !filters.currentLng && saved.currentLat && saved.currentLng
    )
    const needsTurn = withTurn && turn === undefined
    if (!restoresLocation && !needsTurn) return

    const updates = restoresLocation
      ? withNearestFirst({ currentLat: saved.currentLat, currentLng: saved.currentLng }, filters.sort)
      : {}

    setSearchParams(prev => {
      const currentFilters = parseSearchParams(prev)
      const merged = { ...currentFilters, ...updates }
      const currentQ = prev.get('q') || ''
      const currentView = prev.get('view') || ''
      return rebuildSearchParams(
        prev, merged, currentQ, currentView, turnForRewrite(prev, { withTurn, newSearch: false })
      )
    }, { replace: true })
  }, [filters, setSearchParams, syncLocationStorage, withTurn, turn])

  // Persist location to sessionStorage whenever URL filters change.
  // Dates are managed by the rentalDates Redux slice — not stored here.
  useEffect(() => {
    if (!hasRestored.current || !syncLocationStorage) return
    persistSearchFilters(filters)
  }, [filters, syncLocationStorage])

  // Committed search text from URL
  const submittedSearch = searchParams.get('q') || ''

  // The wire filter every listings consumer sends (search page + vendor
  // storefront): the URL filter state minus the UI-only keys the backend's
  // strict schema would 400 on, plus the sanitized committed search.
  // Extracted here (2026-07-27) so pages can't drift into their own
  // stripping logic — a page-local variant with one extra fossil key is
  // exactly how the storefront served an empty grid for weeks.
  const apiFilter = useMemo(() => {
    const { hideMyListings, hideUnavailable, ...filterRest } = filters
    void hideMyListings
    void hideUnavailable
    const result = cleanObj(filterRest)
    // Price bounds: UI state and shareable URLs speak DOLLARS (the
    // slider's human domain); the wire contract
    // (ListingSearchFilterSchema) is Int CENTS like every money field.
    // This memo is the single UI→wire seam, so the conversion lives
    // here — pages sending dollars raw is the audit bug where a $500
    // max compared as 500 cents and excluded everything over $5/day.
    if (result.startPrice != null) {
      result.startPrice = dollarsToCents(result.startPrice)
    }
    if (result.endPrice != null) {
      result.endPrice = dollarsToCents(result.endPrice)
    }
    if (submittedSearch) {
      result.search = sanitizeSearchQuery(submittedSearch)
    }
    return result
  }, [filters, submittedSearch])

  // Local search text for typing (not synced to URL until submit)
  const [searchText, setSearchText] = useState(submittedSearch)

  // View mode: URL param takes precedence, then localStorage, then default
  const view = searchParams.get('view') || localStorage.getItem('listingsView') || 'list'

  // vendorId from URL
  const vendorId = searchParams.get('vendorId') || ''

  /**
   * Update filters — merges updates into current filters and syncs to URL.
   * Uses replace:true so filter tweaks don't create browser history entries.
   * A filter change starts a new search, with a new turn, unless the caller
   * copies values into the search already open (`sameSearch`), as the page
   * does with the dates other pages picked.
   */
  const setFilters = useCallback((updates, { sameSearch = false } = {}) => {
    setSearchParams(prev => {
      const currentFilters = parseSearchParams(prev)
      const merged = { ...currentFilters, ...updates }
      const currentQ = prev.get('q') || ''
      const currentView = prev.get('view') || ''
      return rebuildSearchParams(
        prev, merged, currentQ, currentView, turnForRewrite(prev, { withTurn, newSearch: !sameSearch })
      )
    }, { replace: true })
  }, [setSearchParams, withTurn])

  /**
   * Submit search — writes search text to URL param 'q'.
   */
  const submitSearch = useCallback((text) => {
    setSearchParams(prev => {
      const currentFilters = parseSearchParams(prev)
      const currentView = prev.get('view') || ''
      return rebuildSearchParams(
        prev, currentFilters, text, currentView, turnForRewrite(prev, { withTurn, newSearch: true })
      )
    }, { replace: true })
  }, [setSearchParams, withTurn])

  /**
   * Reset all filters — clears everything except dates.
   * Matches the previous handleFilterReset behavior.
   */
  const resetFilters = useCallback(() => {
    setSearchText('')
    setSearchParams(prev => {
      const currentFilters = parseSearchParams(prev)
      const resetState = {
        startDate: currentFilters.startDate,
        endDate: currentFilters.endDate,
      }
      return rebuildSearchParams(
        prev, resetState, '', '', turnForRewrite(prev, { withTurn, newSearch: true })
      )
    }, { replace: true })
  }, [setSearchParams, withTurn])

  /**
   * Set view mode — updates URL param and persists to localStorage. The
   * same search in another view, so it keeps its turn.
   */
  const setView = useCallback((newView) => {
    localStorage.setItem('listingsView', newView)
    setSearchParams(prev => {
      const currentFilters = parseSearchParams(prev)
      const currentQ = prev.get('q') || ''
      return rebuildSearchParams(
        prev, currentFilters, currentQ, newView, turnForRewrite(prev, { withTurn, newSearch: false })
      )
    }, { replace: true })
  }, [setSearchParams, withTurn])

  return {
    filters,
    apiFilter,
    turn,
    searchText,
    setSearchText,
    submittedSearch,
    submitSearch,
    setFilters,
    resetFilters,
    view,
    setView,
    vendorId,
  }
}
